import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import type { TextItem } from 'pdfjs-dist/types/src/display/api';
import mammoth from 'mammoth';

// Configurar el worker con la URL correcta para la versión 3.11.174
GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

export async function extractTextFromFile(file: File): Promise<string> {
  try {
    const fileType = file.type.toLowerCase();
    const fileName = file.name.toLowerCase();

    // PDF files
    if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
      return extractTextFromPDF(file);
    }

    // Word documents
    if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
        fileType === 'application/msword' || 
        fileName.endsWith('.docx') || 
        fileName.endsWith('.doc')) {
      return extractTextFromWord(file);
    }

    // Text files
    if (fileType === 'text/plain' || fileName.endsWith('.txt')) {
      return extractTextFromTxt(file);
    }

    // RTF files
    if (fileType === 'application/rtf' || fileName.endsWith('.rtf')) {
      return extractTextFromTxt(file);
    }

    throw new Error('Formato de archivo no soportado');
  } catch (error) {
    console.error('Error al extraer texto del archivo:', error);
    throw error;
  }
}

async function extractTextFromPDF(file: File): Promise<string> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await getDocument({ data: arrayBuffer }).promise;
    let fullText = '';
    
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      
      const pageText = textContent.items
        .map((item) => 'str' in item ? item.str : '')
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
      
      fullText += pageText + '\n\n';
    }
    
    return fullText.trim();
  } catch (error) {
    console.error('Error al extraer texto del PDF:', error);
    throw new Error('No se pudo extraer el texto del PDF. Por favor, verifica que el archivo sea válido.');
  }
}

async function extractTextFromWord(file: File): Promise<string> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value.trim();
  } catch (error) {
    console.error('Error al extraer texto del documento Word:', error);
    throw new Error('No se pudo extraer el texto del documento Word.');
  }
}

async function extractTextFromTxt(file: File): Promise<string> {
  try {
    const text = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        const normalizedText = content
          .replace(/\r\n/g, '\n')
          .replace(/\n{3,}/g, '\n\n')
          .replace(/\t/g, '    ')
          .trim();
        resolve(normalizedText);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });

    return text;
  } catch (error) {
    console.error('Error al extraer texto del archivo:', error);
    throw new Error('No se pudo extraer el texto del archivo. Por favor, verifica que el archivo sea válido.');
  }
}
